import get from 'lodash/get'
import { autorun, flow, computed, isObservableMap, makeObservable, observable, action } from 'mobx'
import { PromisedComputed, promisedComputed } from '../../utils/computed-async-mobx'
import { fnFromCode } from '../../utils/fn'
import toJS from '../../utils/tojs'
import { uistore } from '../uistore'
import { createQueryString, IDataTransformer } from './filter'
import { JSProvider } from './javascript'
import type { IMapping } from './mapping'
import { isField } from './../../utils/type-utils'
import type { IField } from './field'
import convertProps, { resolveDataAtFieldPath } from './convert-props'
import { getEnv } from '@invisr/mobx-state-tree'

let jsProvider
const UNBOUND_PARAM = Symbol('Unbound')
const stepsCache = new WeakMap()

const pausedFn = () => uistore?.propsMapperPaused

export default class Mapper {
  error?: {message: string} = null
  errType: 'error'|'warning' = 'error'
  pollId = null
  promised: PromisedComputed<Mapper>
  mapping: IMapping
  overrides: Record<string, Mapper>

  constructor (mapping: IMapping, overrides?: Record<string, Mapper>, key?: string) {
    if(process.env.NODE_ENV === 'development' && !key) {
      key = mapping.fullKey
    }
    makeObservable(this, {
      error: observable,
      setError: action,
      resolvedChildren: computed,
      ds: computed,
      boundFunction: computed,
      busy: computed,
      toProps: computed,
      whenReady: flow.bound
    }, { name: key })
    this.promised = promisedComputed(mapping.ssrProps, this.resolve.bind(this), { pausedFn, name: key && ('async:' + key) })
    this.mapping = mapping
    if (overrides && typeof overrides === 'object') {
      this.overrides = overrides
    } else if (getEnv(this.mapping)?.mappingOverrides) {
      this.overrides = getEnv(this.mapping).mappingOverrides
    }
  }

  poll() {
    if(this.pollId) clearTimeout(this.pollId)
    if(!this.mapping?.intervalMs) return
    this.promised.refreshCallCount
    const interval = this.mapping.intervalMs < 1000 ? 1000 : this.mapping.intervalMs
    this.pollId = setTimeout(() => {
      if(document.hidden || (this.promised.pr && this.promised.pr.state !== 'fulfilled')) {
        this.poll()
      } else {
        this.promised.refresh()
      }
    }, interval)
  }
  
  setError = (e?: { message: string }, errType: 'error'|'warning' = 'error') => {
    if(e && this.overrides && this.mapping.fullKey && errType === 'error') {
      this.overrides.errors = this.overrides.errors || {}
      this.overrides.errors[this.mapping.fullKey] = e
    }
    this.error = e
    this.errType = errType
  }
  
  resolveAsync = () => this.whenReady(true)

  convertProps = this.resolveAsync
  
  getMapper (mapping) {
    if (this.overrides && mapping && !mapping.mapper?.overrides) {
      const mapper = new Mapper(mapping, this.overrides)
      return mapper
    }
    return mapping?.mapper
  }

  shouldResolveChildren () {
    const { children, bindingType } = this.mapping
    return children && !['function', 'multi'].includes(bindingType)
  }

  get resolvedChildren () {
    uistore?.lastRefreshTime
    if (!this.shouldResolveChildren()) return

    const resolved = {}
    for (const c of this.mapping.children) {
      const mapper = this.getMapper(c)
      resolved[c.key] = mapper?.toProps
    }
    return resolved
  }

  resolveData () {
    uistore?.lastRefreshTime
    const data = this.resolveBinding()

    if (this.shouldRunFn(data)) {
      return this.runFn(data)
    }

    return data
  }

  /** by default objects with empty values are dropped. Pass preserveNullValues to get them  */
  resolve (preserveNullValues = false) {
    uistore?.lastRefreshTime
    const children = this.resolvedChildren
    const data = this.resolveData()
    if(data?.then) return data.then(action(data => convertProps.call(this.mapping, data, children, this.setError, preserveNullValues)))
    return convertProps.call(this.mapping, data, children, this.setError, preserveNullValues)
  }

  get ds () {
    const { dataSource } = this.mapping
    if (!dataSource) return null
    if (this.overrides?.[dataSource.id]) return this.overrides[dataSource.id]
    return uistore?.dataSourceFactory?.getInstance(dataSource.id)
  }

  get boundFunction () {
    const { dataSource, value = {}, js, children, componentMapping } = this.mapping
    let f
    if (js && typeof value === 'string') {
      if (!jsProvider) jsProvider = new JSProvider()
      const inputs = children || []
      const code = fnFromCode(value, inputs)
      f = (...params) => jsProvider.runCode(code, inputs, params, this.mapping)
    } else if (dataSource && this.ds) {
      if (this.ds.config.type === 'JavaScript' || this.ds.config.type === 'Function') {
        this.ds.config.code
        this.ds.config.mapping?.toProps
        f = (...params) => this.ds.run(...params)
      } else if (isField(value)) {
        f = get(this.ds, value.fieldPath)
      }
      //      if (typeof f === 'function') f = f.bind(this.ds)
    } else if (componentMapping) {
      f = this.resolveComponentMapping()
    }
    return f
  }

  boundFunctionParams (computed = true) {
    const { children } = this.mapping
    const params = []
    for (const c of children || []) {
      let param
      const mapper = this.getMapper(c)
      if(c.mappingPath && this.overrides?.[c.mappingPath]) {
        param = c.value?.fieldPath ? get(this.overrides[c.mappingPath], c.value?.fieldPath) : this.overrides[c.mappingPath]
      } else {
        const otherMapper = this.getMapper(c.mappedToMapping)
        if (c.bindingType && mapper) {
          if (c.mappedToMapping && otherMapper) {
            param = computed ? otherMapper.toProps : stepsCache.get(c.mappedToMapping)
          } else {
            param = computed ? mapper.toProps : mapper.resolveAsync()
          }
        } else if (!c.children || !c.children.length) {
          param = UNBOUND_PARAM
        }
      }
      params.push(param)
    }
    return params
  }

  binding_function_defer () {
    const f = this.boundFunction
    if (typeof f !== 'function') return

    const fn = async (...p) => {
      let args = []
      const params = this.boundFunctionParams(false)
      if (!params.length) args = p
      else {
        for (const param of params) {
          if (param === UNBOUND_PARAM) args.push(p.shift())
          else {
            args.push(param?.then ? await param : param)
          }
        }
      }

      return f(...args)
    }
    return fn
  }

  binding_function (computed = true) {
    const f = this.boundFunction
    const { children } = this.mapping

    if (typeof f !== 'function') return

    const params = this.boundFunctionParams(computed)
    const fn = async (...p) => {
      let args = []
      
      if(computed) {
        let b = false
        if (children && children.length) {
          for (const c of children) {
            const mapper = this.getMapper(c)
            if(mapper?.busy) b = true
          }
        }
  
        if (b) {
          return this.cached
        }
      }

      if (!params.length) args = p
      else {
        for (const param of params) {
          if (param === UNBOUND_PARAM) args.push(p.shift())
          else args.push(param?.then ? await param : param)
        }
      }
      
      return f(...args)
    }

    return fn
  }

  resolveComponentMapping () {
    const { componentMapping, value } = this.mapping
    const fieldPath = (value as IField).fieldPath;
    let path: string, data;
    if (this.overrides?.[componentMapping.id]) {
      data = this.overrides[componentMapping.id].toProps
      path = fieldPath.replace(/^props\./, '')
      return get(data, path)
    } else if (fieldPath.indexOf('state') === 0 && componentMapping.state) {
      data = componentMapping.state
      if(fieldPath === 'state') return data
      path = fieldPath.replace(/^state\./, '')
      return get(data, path)
    } else if (fieldPath === '$.setState') {
      return componentMapping.setState
    } else {
      path = fieldPath.replace(/^props\./, '')
      let key = path?.match(/^[A-Za-z_$0-9]+/)?.[0]
      if(key) {
        const mapper = this.getMapper(componentMapping.mapping?.asObject?.[key])
        if(!mapper) {
          return componentMapping.renderProps?.[key]
        } else if(mapper === this) {
          data = this.cached
          return get(data, path)
        } else {
          mapper?.toProps
          return mapper?.resolveAsync().then(props => {
            data = { [key]: props || componentMapping.renderProps?.[key] }
            return get(data, path)
          })
        }
      }      
    }
  }

  binding_data () {
    const { mappedToMapping, mappingPath, componentMapping, dataSource, value = {} } = this.mapping
    if (dataSource && this.ds && this.mapping.parentDataSource?.id !== dataSource.id) {
      this.ds.data && this.ds.data.length && this.ds.data[0]
      if (this.mapping.intervalMs) {
        uistore?.dataSourceFactory.reloadData([this.ds], null, true)
      }
      const pending = uistore?.dataSourceFactory.pending?.[dataSource.id]
      const f = () => isField(value) ? resolveDataAtFieldPath(this.ds.data, value.fieldPath) : this.ds.data

      return pending ? () => pending.then(f) : f()
    } else if(!dataSource) {

      if (componentMapping && isField(value)) {
        return this.resolveComponentMapping()
      }

      if(mappingPath && this.overrides?.[mappingPath]) {
        return value?.fieldPath ? get(this.overrides[mappingPath], [value?.fieldPath]) : this.overrides[mappingPath]
      }

      if (mappedToMapping) {
        return stepsCache.get(mappedToMapping)
      }

    }
  }

  binding_value (computed = true) {
    const { type, value } = this.mapping
    toJS(value)
    if (type === 'Transformation') {
      if (computed) {
        return {
          fields: value && toJS((value as IDataTransformer).queryFields),
          query: value && (value as IDataTransformer).qString
        }
      } else {
        return async () => ({
          fields: value && toJS((value as IDataTransformer).queryFields),
          query: value && (await createQueryString(value))
        })
      }
    } else if (type === 'Filter') {
      if (computed) {
        return {...value, where: value.where.resolved}
      } else {
        return async () => ({...value, where: value.where.toSFasync})
      }
    }

    return value
  }

  binding_multi () {
    const { children } = this.mapping
    if (!children) return
    const params = []

    for (const c of children) {
      const mapper: Mapper = this.getMapper(c)
      if (mapper.busy) return
      params.push([c, mapper.toProps])
    }

    return async (...p) => {
      let lastResult
      for (const [step, val] of params) {
        if (typeof val === 'function') lastResult = await val(...p)
        else lastResult = val
        stepsCache.set(step, lastResult)
      }
      return lastResult
    }
  }

  binding_multi_defer () {
    const { children } = this.mapping
    if (!children) return

    return async (...p) => {
      let lastResult
      for (const step of children) {
        const mapper = this.getMapper(step)
        const f = await mapper.resolveAsync()
        if (typeof f === 'function') lastResult = await f(...p)
        else lastResult = f
        stepsCache.set(step, lastResult)
      }
      return lastResult
    }
  }

  resolveBinding (computed = true) {
    const { value, bindingType, type } = this.mapping
    if (this.mapping.isBoundToRef) {
      const fieldPath = (value as IField).fieldPath;
      if(this.ds) {
        if (fieldPath === '$') return this.ds
        return get(this.ds, fieldPath.replace(/^\$\./, ''))
      }
    }

    if (bindingType) {
      let fn = this['binding_' + bindingType]
      if (type === 'func') {
        if (bindingType === 'function') fn = this.binding_function_defer
        else if (bindingType === 'multi') fn = this.binding_multi_defer
      }
      return fn && fn.call(this, computed)
    }
  }

  async runFn (fn) {
    try {
      const res = await (fn.then ? fn : fn())
      this.setError()
      return res
    } catch (e) {
      this.setError(e)
    }
  }

  shouldRunFn (fn) {
    if (typeof fn === 'function' && this.mapping.type !== 'func' || fn?.then) {
      return true
    }
    return false
  }

  whenReady = function * whenReady(this: Mapper, keepAlive = true) {
    let disposer
    if(keepAlive) disposer = autorun(() => this.toProps)
    for(let c of this.mapping.children || []) {
      let m = this.getMapper(c)
      if(m) yield m.whenReady(false)
    }
    
    if (!this.mapping.dataSource && this.mapping.componentMapping && isField(this.mapping.value)) {
      yield this.resolveComponentMapping()
    } else if(this.mapping.dataSource && uistore?.dataSourceFactory?.pending?.[this.mapping.dataSource.id]) {
      yield uistore?.dataSourceFactory?.pending?.[this.mapping.dataSource.id]
    }

    const value = ((this.mapping.value as unknown) as Mapper);
    if(value?.whenReady) {
      yield value.whenReady();
    }

    yield this.promised.currentState
    
    disposer?.()
    return this.toProps
  }

  get busy () {
    if (uistore?.propsMapperPaused) return true

    if (this.mapping.type === 'Transformation' && this.mapping.value) {
      return (this.mapping.value as IDataTransformer).busy
    }
    let b = false
    for(let c of this.mapping.children || []) {
      if(c.mapper?.busy) b = true
    }
    return this.promised.busy || b
  }

  get toProps () {
    this.poll()
    return this.promised.value
  }

  get cached (): Mapper|undefined {
    return (this.promised.cached as any)?.value
  }
}
